import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { LuPlus, LuSearch, LuStar, LuChevronLeft, LuChevronRight, LuPencil, LuTrash2 } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import ConfirmationModal from '../components/ConfirmationModal';
import NovoAgenteModal from '../components/NovoAgenteModal';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './SystemPromptsPage.css';
import {
    RegrasIAService, TOM_VOZ_OPTIONS, OBJETIVO_PRINCIPAL_OPTIONS,
    type AgenteListItem,
} from '../services/RegrasIAService';

const POR_PAGINA = [25, 50, 100];

type Filtro = 'todos' | 'respondendo' | 'pausados';

const rotuloDe = (opcoes: readonly { value: number; label: string }[], valor?: number) =>
    (valor === undefined || valor === null) ? '' : (opcoes.find(o => o.value === valor)?.label ?? '');

const iniciais = (nome: string) => (nome || '').trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const SystemPromptsPage = () => {
    const navigate = useNavigate();
    const celular = useCelular();

    const [agentes, setAgentes] = useState<AgenteListItem[]>([]);
    const [carregando, setCarregando] = useState(true);
    /* Esta tela SAI para o editor (/chat-rules/:id) e volta. */
    const [recorte, gravarRecorte] = useRecorte('sp:recorte', {
        busca: '', filtro: 'todos' as Filtro, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [filtro, setFiltro] = useState<Filtro>(recorte.filtro);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, filtro, pagina, porPagina });
    }, [busca, filtro, pagina, porPagina, gravarRecorte]);
    const [alternando, setAlternando] = useState<number | null>(null);

    /* "Novo agente" abre a galeria de modelos em vez de ir direto para o
       formulário em branco — desenho de 2026-09-12,
       docs/designsystem/propostas/agentes-prontos.html. A rota /novo continua
       existindo: é o "começar do zero" de dentro da modal, e o destino de quem
       tem o link salvo. */
    const [modalAberta, setModalAberta] = useState(false);

    /* Exclusão: guarda o agente inteiro, não só o id, porque a confirmação
       precisa do nome e da contagem de conexões para dizer o que vai acontecer. */
    const [paraExcluir, setParaExcluir] = useState<AgenteListItem | null>(null);
    const [excluindo, setExcluindo] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const resposta = await RegrasIAService.getAgentes();
            if (resposta.sucesso) setAgentes(resposta.dados || []);
            else aviso.erro(resposta.mensagem || 'Não foi possível carregar os agentes.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const contagens = useMemo(() => ({
        todos: agentes.length,
        respondendo: agentes.filter(a => a.botAtivo).length,
        pausados: agentes.filter(a => !a.botAtivo).length,
    }), [agentes]);

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return agentes.filter(a => {
            if (filtro === 'respondendo' && !a.botAtivo) return false;
            if (filtro === 'pausados' && a.botAtivo) return false;
            if (termo && !(a.nomeAgente || '').toLowerCase().includes(termo)) return false;
            return true;
        });
    }, [agentes, busca, filtro]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, filtro, porPagina]);

    /* O interruptor fica na linha porque pausar a IA é a ação que se faz
       sem querer abrir nada. `stopPropagation` para não abrir o agente junto. */
    const alternarBot = async (evento: React.MouseEvent, agente: AgenteListItem) => {
        evento.stopPropagation();
        const novo = !agente.botAtivo;
        setAlternando(agente.id);
        try {
            const r = await RegrasIAService.toggleBot(agente.id, novo);
            if (r.sucesso) {
                setAgentes(prev => prev.map(a => a.id === agente.id ? { ...a, botAtivo: novo } : a));
                aviso.ok(novo ? 'Agente respondendo.' : 'Agente pausado.');
            } else aviso.erro(r.mensagem || 'Não foi possível alterar o estado do bot.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setAlternando(null);
        }
    };

    /* Por que a lixeira às vezes aparece desligada, em vez de sumir: a API
       recusa excluir o agente padrão e o único agente da empresa (medido em
       IaRegraService.ExcluirConfigAgente), e cada recusa tem motivo próprio.
       Some, a pessoa procura o botão; desligada com o motivo no title, ela
       descobre o que fazer antes. */
    const motivoParaNaoExcluir = (agente: AgenteListItem): string | null => {
        if (agentes.length === 1) return 'Não dá para excluir o único agente da empresa. Crie outro antes.';
        if (agente.isDefault) return 'Este é o agente padrão. Defina outro como padrão antes de excluí-lo.';
        return null;
    };

    const excluir = async () => {
        if (!paraExcluir) return;
        setExcluindo(true);
        try {
            const r = await RegrasIAService.deleteConfigAgente(paraExcluir.id);
            if (r.sucesso) {
                aviso.ok('Agente excluído.');
                setParaExcluir(null);
                await carregar();
            } else {
                // As recusas da API (padrão, único) já vêm com texto próprio.
                aviso.erro(r.mensagem || 'Não foi possível excluir o agente.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setExcluindo(false);
        }
    };

    /* Sem colunas de Regras e FAQ: o endpoint da lista devolve as duas
       listas vazias (ver a nota em AgenteListItem), e buscar a contagem
       daria uma chamada por agente. Elas aparecem ao abrir o agente. */
    const COLUNAS = '1.6fr 1.1fr 1.2fr .9fr 1.2fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {agentes.length === 0 ? 'Nenhum agente cadastrado' : 'Nada com esse filtro'}
            </div>
            <p className="lk-blank-text">
                {agentes.length === 0
                    ? 'Um agente é o conjunto de instruções que a IA segue ao responder seus clientes.'
                    : 'Ajuste a busca ou o filtro para ver os outros.'}
            </p>
            {agentes.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={() => setModalAberta(true)}>
                    <LuPlus size={14} /> Novo agente
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Agentes de IA"
                subtitle="Gerencie os agentes da sua empresa e escolha o padrão."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!carregando && agentes.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={() => setModalAberta(true)}>
                            <LuPlus size={14} /> Novo agente
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome do agente…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>

                    {([
                        ['todos', 'Todos'], ['respondendo', 'Respondendo'], ['pausados', 'Pausados'],
                    ] as [Filtro, string][]).map(([id, rotulo]) => (
                        <button key={id} type="button"
                            className={`lk-pill ${filtro === id ? 'is-on' : ''}`}
                            onClick={() => setFiltro(id)}>
                            {rotulo} <b>{contagens[id]}</b>
                        </button>
                    ))}
                </div>

                {/* Celular: a grade de seis colunas não cabe em 358px, então a
                    linha vira cartão — quem é o agente e em que pé ele está.
                    Tom, objetivo e conexões ficam na bancada, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {carregando
                                ? Array.from({ length: 5 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(a => {
                                    const tom = rotuloDe(TOM_VOZ_OPTIONS, a.tomVoz);
                                    const objetivo = rotuloDe(OBJETIVO_PRINCIPAL_OPTIONS, a.objetivoPrincipal);
                                    return (
                                        <ListCard
                                            key={a.id}
                                            avatar={<span className="lk-ava" aria-hidden="true">{iniciais(a.nomeAgente)}</span>}
                                            titulo={a.nomeAgente}
                                            apoio={[objetivo, tom].filter(Boolean).join(' · ') || 'sem objetivo definido'}
                                            estado={
                                                <>
                                                    {/* O interruptor vem inteiro, e não só o rótulo: pausar
                                                        a IA é o que mais se faz pelo celular, e ele já para
                                                        a propagação para não abrir a bancada junto. */}
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={a.botAtivo}
                                                        aria-label={a.botAtivo ? 'Pausar o agente' : 'Ativar o agente'}
                                                        className={`sp-switch ${a.botAtivo ? 'is-on' : ''}`}
                                                        disabled={alternando === a.id}
                                                        onClick={e => alternarBot(e, a)}
                                                    >
                                                        <span className="sp-switch-track"><i /></span>
                                                        <span className="sp-switch-txt">
                                                            {a.botAtivo ? 'Respondendo' : 'Pausado'}
                                                        </span>
                                                    </button>
                                                    {a.isDefault && (
                                                        <span className="sp-padrao"><LuStar size={10} /> Padrão</span>
                                                    )}
                                                </>
                                            }
                                            label={`Configurar ${a.nomeAgente}`}
                                            onOpen={() => navigate(`/chat-rules/${a.id}`)}
                                            acoes={
                                                <RowAction
                                                    icon={<LuTrash2 size={15} />}
                                                    label={motivoParaNaoExcluir(a) ?? `Excluir o agente ${a.nomeAgente}`}
                                                    disabled={!!motivoParaNaoExcluir(a)}
                                                    danger
                                                    onClick={() => setParaExcluir(a)}
                                                />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!carregando && filtrados.length === 0 && listaVazia}
                        {!carregando && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Agente</div>
                            <div className="lk-head">Tom de voz</div>
                            <div className="lk-head">Objetivo</div>
                            <div className="lk-head">Conexões</div>
                            <div className="lk-head">Bot</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 5 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(a => {
                                    const tom = rotuloDe(TOM_VOZ_OPTIONS, a.tomVoz);
                                    const objetivo = rotuloDe(OBJETIVO_PRINCIPAL_OPTIONS, a.objetivoPrincipal);
                                    const conexoes = a.conexoesVinculadas?.length ?? 0;
                                    /* Sem clique na linha e sem olho, ao contrário das outras
                                       listagens: aqui não existe "ver" — o agente abre numa
                                       PÁGINA de configuração, não num painel. Um olho levaria
                                       para o editor e mentiria sobre o que faz. */
                                    return (
                                        <ListRow key={a.id} columns={COLUNAS}>
                                            <span className="lk-cell lk-strong">
                                                <span>{a.nomeAgente}</span>
                                                {a.isDefault && (
                                                    <span className="sp-padrao"><LuStar size={10} /> Padrão</span>
                                                )}
                                            </span>
                                            <span className={`lk-cell ${tom ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{tom || '—'}</span>
                                            </span>
                                            <span className={`lk-cell ${objetivo ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{objetivo || '—'}</span>
                                            </span>
                                            <span className={`lk-cell ${conexoes ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{conexoes || 'nenhuma'}</span>
                                            </span>
                                            <span className="lk-cell">
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={a.botAtivo}
                                                    aria-label={a.botAtivo ? 'Pausar o agente' : 'Ativar o agente'}
                                                    className={`sp-switch ${a.botAtivo ? 'is-on' : ''}`}
                                                    disabled={alternando === a.id}
                                                    onClick={e => alternarBot(e, a)}
                                                >
                                                    <span className="sp-switch-track"><i /></span>
                                                    <span className="sp-switch-txt">
                                                        {a.botAtivo ? 'Respondendo' : 'Pausado'}
                                                    </span>
                                                </button>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuPencil size={14} />} label="Configurar agente"
                                                    onClick={() => navigate(`/chat-rules/${a.id}`)} />
                                                <RowAction
                                                    icon={<LuTrash2 size={14} />}
                                                    label={motivoParaNaoExcluir(a) ?? `Excluir o agente ${a.nomeAgente}`}
                                                    disabled={!!motivoParaNaoExcluir(a)}
                                                    danger
                                                    onClick={() => setParaExcluir(a)}
                                                />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!carregando && filtrados.length === 0 && listaVazia}
                    {!carregando && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <NovoAgenteModal
                aberta={modalAberta}
                onFechar={() => setModalAberta(false)}
                onComecarDoZero={() => { setModalAberta(false); navigate('/chat-rules/novo'); }}
                /* O agente já vem criado e salvo da modal — aqui é só ir para a
                   bancada dele, que abre com FAQ e Regras destravadas porque
                   agora existe id. */
                onCriado={agente => { setModalAberta(false); navigate(`/chat-rules/${agente.id}`); }}
            />

            {/* A mensagem diz a consequência que ninguém adivinha: a conexão
                vinculada não some junto — ela fica SEM agente e passa a usar o
                padrão da empresa (FK com SET NULL, conferido no AppDbContext).
                Regras e perguntas vão junto, por cascade. */}
            <ConfirmationModal
                isOpen={!!paraExcluir}
                onClose={() => setParaExcluir(null)}
                onConfirm={excluir}
                isConfirming={excluindo}
                title="Excluir agente"
                confirmText="Excluir agente"
                message={paraExcluir
                    ? `O agente "${paraExcluir.nomeAgente}" será excluído com as regras e as perguntas frequentes dele. Isso não pode ser desfeito.`
                        + ((paraExcluir.conexoesVinculadas?.length ?? 0) > 0
                            ? ` ${paraExcluir.conexoesVinculadas!.length === 1
                                ? 'A conexão vinculada a ele passa'
                                : `As ${paraExcluir.conexoesVinculadas!.length} conexões vinculadas a ele passam`} a responder com o agente padrão da empresa.`
                            : '')
                    : ''}
            />
        </div>
    );
};

export default SystemPromptsPage;
